import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Cpu, Palette, Shield, Sparkles, Footprints } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { PhotoPlaceholder } from "../components/PhotoPlaceholder";
import { PlaceholderNote } from "../components/ComingSoon";
import { ABOUT_PAGE, GALLERY, IMAGES } from "../content/site";

const Section = ({ children, className = "", id, labelledBy }) => (
  <section id={id} aria-labelledby={labelledBy} className={`px-4 py-14 sm:px-8 sm:py-20 lg:px-16 ${className}`}>
    <div className="mx-auto max-w-7xl">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-gold-dark sm:text-sm">{children}</p>
);

const MEMORIES = [
  { icon: Footprints, text: "Spontaneous from the time he learned to walk" },
  { icon: Sparkles, text: "Welcoming to people and comfortable meeting strangers" },
  { icon: Shield, text: "Dependable and protective of his family" },
  { icon: Cpu, text: "Skilled with technology and electronics" },
  { icon: Palette, text: "A talented artist" },
];

const DROP_CAP =
  "first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:font-serif first-letter:text-5xl first-letter:font-medium first-letter:leading-[0.85] first-letter:text-gold-dark sm:first-letter:text-6xl";

const MothersStory = () => {
  const [mode, setMode] = useState("short"); // "short" excerpt visible by default | "full"
  const fullRef = useRef(null);
  const hasShort = Boolean(ABOUT_PAGE.storyShort && ABOUT_PAGE.storyShort.trim());
  const hasFull = Array.isArray(ABOUT_PAGE.storyFull) && ABOUT_PAGE.storyFull.length > 0;

  useEffect(() => {
    if (mode === "full" && fullRef.current) {
      fullRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [mode]);

  const backToStart = () => {
    setMode("short");
    document.getElementById("mothers-story")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Section labelledBy="mothers-story-heading" className="bg-paper/70" id="mothers-story">
      <div className="mx-auto max-w-3xl">
        <Reveal>
          <Eyebrow>A mother's story of love, loss, grief, and purpose</Eyebrow>
          <h2 id="mothers-story-heading" className="font-serif text-2xl font-normal leading-snug tracking-tight text-ink sm:text-3xl lg:text-4xl">
            My Son's Story
          </h2>
          <div className="mt-6 rounded-xl border-l-4 border-gold bg-white p-5 text-base leading-relaxed text-body" role="note" data-testid="mothers-story-content-note">
            This story includes a mother's account of suicide loss and grief. The beginning
            is below — continue to the full story whenever you are ready.
          </div>
        </Reveal>

        {mode === "short" && (
          <div className="mt-8" data-testid="mothers-story-short-content">
            {hasShort ? (
              <div className="rounded-2xl border border-sage/10 bg-white p-6 sm:p-10">
                {ABOUT_PAGE.storyShort.split("\n").filter(Boolean).map((p, i) => (
                  <p key={i} className={`mb-5 text-base leading-relaxed text-body last:mb-0 ${i === 0 ? DROP_CAP : ""}`}>{p}</p>
                ))}
              </div>
            ) : (
              <PlaceholderNote testId="mothers-story-short-placeholder">
                <strong className="font-semibold text-ink">Placeholder — awaiting the family's words.</strong>{" "}
                A short introduction to TJ's story, written in his mother's own voice, will
                be placed here when the family is ready to share it.
              </PlaceholderNote>
            )}
            {hasFull && (
              <div className="mt-8 text-center">
                <button
                  type="button"
                  onClick={() => setMode("full")}
                  className="inline-flex min-h-[48px] items-center rounded-full bg-sage px-7 py-3 text-sm font-semibold text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-sage-light"
                  data-testid="mothers-story-continue-full"
                >
                  Continue reading the full story
                </button>
              </div>
            )}
          </div>
        )}

        {mode === "full" && (
          <article ref={fullRef} className="mt-10 scroll-mt-28 space-y-8" data-testid="mothers-story-full-content">
            {hasFull ? (
              ABOUT_PAGE.storyFull.map((section, i) => {
                const paragraphs = (section.body || "").split("\n").filter(Boolean);
                const isFirst = i === 0;
                const isLast = i === ABOUT_PAGE.storyFull.length - 1;
                return (
                  <Fragment key={i}>
                    <section aria-label={section.heading} className="rounded-2xl border border-sage/10 bg-white p-6 sm:p-10" data-testid={`mothers-story-chapter-${i + 1}`}>
                      <div aria-hidden="true" className="mb-4 h-px w-12 bg-gold/70" />
                      <h3 className="font-serif text-xl font-medium italic leading-snug text-ink sm:text-2xl">{section.heading}</h3>
                      {paragraphs.map((p, j) => {
                        if (isLast && j === paragraphs.length - 1) {
                          return (
                            <p key={j} className="mt-8 border-t border-sage/10 pt-8 text-center font-serif text-xl italic leading-relaxed text-ink sm:text-2xl">
                              {p}
                            </p>
                          );
                        }
                        return (
                          <p key={j} className={`mt-4 text-base leading-relaxed text-body ${isFirst && j === 0 ? DROP_CAP : ""}`}>
                            {p}
                          </p>
                        );
                      })}
                    </section>
                    {i === 3 && ABOUT_PAGE.storyFull.length > 5 && (
                      <div className="rounded-xl border border-gold/30 bg-gold-soft/40 p-5 text-center" role="note" data-testid="mothers-story-mid-pause">
                        <p className="font-serif text-base italic leading-relaxed text-body">
                          You can pause here anytime. If this story brings things up for you,
                          support is available — call or text{" "}
                          <a href="tel:988" className="font-semibold text-sage underline underline-offset-4" data-testid="mothers-story-mid-pause-988">
                            988
                          </a>
                          .
                        </p>
                      </div>
                    )}
                  </Fragment>
                );
              })
            ) : (
              <>
                <PlaceholderNote testId="mothers-story-full-placeholder">
                  <strong className="font-semibold text-ink">Placeholder — awaiting the family's words.</strong>{" "}
                  The full story will be published here in the mother's own voice, exactly as
                  she chooses to share it. Nothing has been written on her behalf. The layout
                  below shows how her story will be presented — in short, gentle sections, with
                  room to pause.
                </PlaceholderNote>
                {["Who TJ was to us", "The days we miss him most", "Why we are speaking now", "What we hope for you"].map((heading) => (
                  <section key={heading} aria-label={heading} className="rounded-2xl border border-sage/10 bg-white p-6 sm:p-8">
                    <h3 className="font-serif text-xl font-medium text-ink sm:text-2xl">{heading}</h3>
                    <p className="mt-3 text-sm italic text-muted-foreground">
                      This section will hold part of TJ's mother's story, in her own words.
                    </p>
                  </section>
                ))}
              </>
            )}
            <div className="rounded-xl bg-sage-soft p-6 text-center">
              <p className="font-serif text-lg italic text-sage">
                You can pause here anytime. If this story brings things up for you, support
                is available — call or text 988.
              </p>
              <Link to="/support" className="mt-4 inline-flex min-h-[44px] items-center rounded-full bg-sage px-6 py-2.5 text-sm font-semibold text-cream hover:bg-sage-light" data-testid="mothers-story-support-link">
                Find support
              </Link>
              <div className="mt-4">
                <button type="button" onClick={backToStart} className="text-sm font-semibold text-sage underline underline-offset-4" data-testid="mothers-story-close-full">
                  Return to the beginning of the story
                </button>
              </div>
            </div>
          </article>
        )}
      </div>
    </Section>
  );
};

export default function About() {
  return (
    <>
      <PageHero eyebrow="About TJ" title="Remembering TJ" testId="about-hero">
        <p>{ABOUT_PAGE.intro}</p>
      </PageHero>

      <Section labelledBy="who-tj-was-heading">
        <Reveal>
          <Eyebrow>Who TJ was</Eyebrow>
          <h2 id="who-tj-was-heading" className="font-serif text-2xl font-normal leading-snug tracking-tight text-ink sm:text-3xl lg:text-4xl">
            A life full of light
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
          <Reveal className="lg:col-span-5">
            {IMAGES.tjPortraitUrl ? (
              <figure data-testid="about-tj-portrait">
                <img
                  src={IMAGES.tjPortraitUrl}
                  alt={IMAGES.tjPortraitAlt}
                  className="w-full rounded-3xl border border-sage/10 object-cover shadow-xl aspect-[4/5]"
                  loading="lazy"
                />
                <figcaption className="mt-3 text-center text-sm text-muted-foreground">
                  TJ — remembered with love
                </figcaption>
              </figure>
            ) : (
              <PhotoPlaceholder
                alt="Family-approved photograph of TJ — to be supplied by the family"
                ratio="aspect-[4/5]"
                testId="about-tj-portrait"
              />
            )}
          </Reveal>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-7">
            {MEMORIES.map(({ icon: Icon, text }, i) => (
              <Reveal key={text} delay={i * 0.06}>
                <div className="flex h-full items-start gap-3 rounded-2xl border border-sage/10 bg-white p-5" data-testid={`memory-card-${i + 1}`}>
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-gold-dark" aria-hidden="true" />
                  <p className="text-base leading-relaxed text-body">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Section labelledBy="life-photos-heading">
        <Reveal>
          <Eyebrow>His life in photos</Eyebrow>
          <h2 id="life-photos-heading" className="font-serif text-2xl font-normal leading-snug tracking-tight text-ink sm:text-3xl lg:text-4xl">
            Moments worth remembering
          </h2>
        </Reveal>
        <div className="mt-10 columns-2 gap-4 sm:gap-6 md:columns-3 lg:columns-5">
          {GALLERY.filter((g) => g.src).map((g, i) => (
            <Reveal key={g.src} delay={i * 0.06} className="mb-4 break-inside-avoid sm:mb-6">
              <figure data-testid={`about-life-photo-${i + 1}`}>
                <img
                  src={g.src}
                  alt={g.alt}
                  className="w-full rounded-2xl border border-sage/10 shadow-sm"
                  loading="lazy"
                />
                <figcaption className="mt-2 text-center text-sm text-muted-foreground">{g.caption}</figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </Section>

      <MothersStory />

      <Section labelledBy="why-heading">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <Eyebrow>Why TJ's Light exists</Eyebrow>
            <h2 id="why-heading" className="font-serif text-2xl font-normal leading-snug tracking-tight text-ink sm:text-3xl lg:text-4xl">
              Turning love and loss into support for others
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-body">{ABOUT_PAGE.whyText}</p>
          </Reveal>
        </div>
      </Section>

      <Section labelledBy="mission-heading" className="bg-sage text-cream">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal>
            <h2 id="mission-heading" className="font-serif text-2xl font-normal leading-snug tracking-tight sm:text-3xl lg:text-4xl">
              Our mission
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-cream/85">{ABOUT_PAGE.missionText}</p>
            <blockquote className="mt-10 rounded-2xl border border-gold/30 bg-sage-light/30 p-8">
              <p className="font-serif text-2xl font-light italic leading-snug sm:text-3xl">
                "{ABOUT_PAGE.quote}"
              </p>
              <footer className="mt-4 text-sm font-semibold uppercase tracking-[0.2em] text-gold-soft">
                {ABOUT_PAGE.quoteAttribution}
              </footer>
            </blockquote>
          </Reveal>
        </div>
      </Section>

      <Section labelledBy="faith-heading" className="bg-gold-soft/30">
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-dark">A verse the family holds close</p>
          <blockquote className="mt-6">
            <p id="faith-heading" className="font-serif text-2xl font-light italic leading-snug text-ink sm:text-3xl">
              "{ABOUT_PAGE.faithVerse}"
            </p>
            <footer className="mt-3 text-sm text-muted-foreground">{ABOUT_PAGE.faithRef}</footer>
          </blockquote>
          <p className="mt-6 text-sm leading-relaxed text-body">{ABOUT_PAGE.faithNote}</p>
        </Reveal>
      </Section>

      <Section labelledBy="keep-shining-heading">
        <Reveal className="mx-auto max-w-3xl text-center">
          <h2 id="keep-shining-heading" className="font-serif text-2xl font-normal leading-snug tracking-tight text-ink sm:text-3xl lg:text-4xl">
            Keep TJ's light shining
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-body">{ABOUT_PAGE.keepText}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/support" className="inline-flex min-h-[48px] items-center rounded-full bg-sage px-7 py-3 text-sm font-semibold text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-sage-light" data-testid="about-find-support-button">
              Find support
            </Link>
            <Link to="/get-involved#donate" className="inline-flex min-h-[48px] items-center rounded-full border border-sage/30 px-7 py-3 text-sm font-semibold text-sage transition-all duration-300 hover:-translate-y-0.5 hover:bg-sage-soft" data-testid="about-support-mission-button">
              Support the mission
            </Link>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
